import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Eye, FilePenLine, AArrowDown, AArrowUp, Filter, MinusCircle, PlusCircle, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { getUsers } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import { createInboundShipment, listAllInventoryItems, getInventoryLogs, downloadShipmentImage } from "../../../reducers/warehouse.slice";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import DataTable from "../../../components/shared/DataTable";
import PageHeader from "../../../components/shared/PageHeader";
import SearchBar from "../../../components/shared/SearchBar";
import RefreshButton from "../../../components/shared/RefreshButton";
import useMediaQuery from "../../../components/shared/useMediaQuery";
import { convertDateToCT } from "../../../utils/util";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../../../components/ui/dropdown-menu";

const blockInvalidInt = (e) => {
  const invalidChars = ["e", "E", ".", ",", "+", "-"];
  if (
    invalidChars.includes(e.key) ||
    (!/^\d$/.test(e.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
      !e.ctrlKey &&
      !e.metaKey)
  ) {
    e.preventDefault();
  }
};

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

const WarehouseInventory = () => {
  const user = getUsers();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const userData = JSON.parse(localStorage.getItem("user"));

  const [showLoader, setShowLoader] = useState(false);
  const [inventoryItemDetails, setInventoryItemDetails] = useState([]);
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("warehouseInventory_sortDirection") || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openProductDetailsDialog, setOpenProductDetailsDialog] = useState(false);
  const [productDetails, setProductDetails] = useState({});
  const [quantity, setQuantity] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [viewUpc, setViewUpc] = useState(null);
  const [viewLogs, setViewLogs] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewLogPage, setViewLogPage] = useState(0);
  const [viewLogRpp, setViewLogRpp] = useState(10);
  const [viewImagesOpen, setViewImagesOpen] = useState(false);
  const [viewImages, setViewImages] = useState([]);
  const [viewImageIdx, setViewImageIdx] = useState(0);

  const fetchInventoryLogs = (upc) => {
    setViewLoading(true);
    dispatch(getInventoryLogs(upc)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setViewLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedData = res?.payload?.map((item) => ({
        ...item,
        createdDate: convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm A"),
      }));
      setViewLogs(formattedData || []);
      setViewLogPage(0);
      setViewLoading(false);
    });
  };

  const handleViewInventory = (row) => {
    setViewUpc(row.upc);
    setViewLogs([]);
    fetchInventoryLogs(row.upc);
  };

  const handleViewLogImages = (rowId) => {
    setViewLoading(true);
    dispatch(downloadShipmentImage(rowId)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setViewLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setViewImages(res?.payload || []);
      setViewImageIdx(0);
      setViewImagesOpen(true);
      setViewLoading(false);
    });
  };

  const viewLogPaginatedRows = viewLogs.slice(viewLogPage * viewLogRpp, viewLogPage * viewLogRpp + viewLogRpp);

  const viewLogColumns = [
    { key: "createdDate", label: "Time Stamp", width: 180 },
    {
      key: "status",
      label: "Type",
      width: 100,
      render: (row) => (
        <span style={{ color: row?.status === "DAMAGED" ? "#FF3D00" : row?.status === "GOOD" ? "#4CAF50" : "inherit", fontWeight: 600 }}>
          {row?.status}
        </span>
      ),
    },
    {
      key: "inboundOutbound",
      label: "Inbound / Outbound",
      width: 150,
      render: (row) => <span>{row?.adjustment_type === "ADD" ? "Inbound" : "Outbound"}</span>,
    },
    { key: "quantity", label: "QTY", width: 70, align: "center" },
    { key: "serial_no", label: "Serial No.", width: 110 },
    {
      key: "change_type",
      label: "Change Type",
      width: 140,
      render: (row) => <span>{row?.change_type === "MANUAL" ? "Manual Adjustment" : "Scan"}</span>,
    },
    { key: "updated_by", label: "Updated By", width: 120 },
    { key: "notes", label: "Notes", width: 140 },
    {
      key: "action",
      label: "",
      width: 60,
      align: "center",
      render: (row) =>
        row?.status === "DAMAGED" ? (
          <Button variant="ghost" size="icon-sm" title="View images" onClick={() => handleViewLogImages(row?.id)}>
            <Images size={16} />
          </Button>
        ) : null,
    },
  ];

  const viewProductName = viewLogs[0]?.item_name;
  const viewProductUpc = viewLogs[0]?.upc;

  const getAllItems = () => {
    setShowLoader(true);
    dispatch(listAllInventoryItems()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedOrder = res?.payload?.map((item) => ({
        ...item,
        id: `${item.upc}-${item + 1}`,
      }));
      setShowLoader(false);
      setInventoryItemDetails(formattedOrder);
      setCurrentPage(0);
    });
  };

  useEffect(() => { getAllItems(); }, []);

  useEffect(() => {
    if (sortDirection) sessionStorage.setItem("warehouseInventory_sortDirection", sortDirection);
    else sessionStorage.removeItem("warehouseInventory_sortDirection");
  }, [sortDirection]);

  const inventoryStats = useMemo(() => ({
    totalItems: inventoryItemDetails.length,
    totalQty: inventoryItemDetails.reduce((sum, d) => sum + (Number(d.quantity) || 0), 0),
  }), [inventoryItemDetails]);

  const filteredData = useMemo(() => {
    let data = [...inventoryItemDetails];
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      data = data.filter(
        (item) =>
          (item.item_name && item.item_name.toLowerCase().includes(t)) ||
          (item.upc && String(item.upc).toLowerCase().includes(t))
      );
    }
    if (sortDirection === "asc") data.sort((a, b) => Number(a?.quantity || 0) - Number(b?.quantity || 0));
    else if (sortDirection === "desc") data.sort((a, b) => Number(b?.quantity || 0) - Number(a?.quantity || 0));
    return data;
  }, [inventoryItemDetails, searchTerm, sortDirection]);

  const paginatedRows = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const handleProductDetailsDialogOpen = (row) => {
    setProductDetails(row);
    setQuantity(0);
    setOpenProductDetailsDialog(true);
  };

  const handleProductDetailsDialogClose = () => { setOpenProductDetailsDialog(false); };

  const handleIncrease = () => { setQuantity((prev) => { const n = Number(prev) + 1; return n < 0 ? 0 : n; }); };
  const handleDecrease = () => { setQuantity((prev) => { const n = Number(prev) - 1; return n < 0 ? 0 : n; }); };

  const handleProductAdd = () => {
    setInventoryItemDetails((prev) =>
      prev.map((item) =>
        item.id === productDetails.id
          ? { ...item, ...productDetails, quantity: Number(productDetails.quantity) }
          : item
      )
    );
    const request_list = [{
      upc: productDetails?.upc,
      quantity: quantity,
      item_name: productDetails?.item_name,
      status: productDetails?.status ? "GOOD" : "DAMAGED",
      change_type: "MANUAL",
      adjustment_type: "ADD",
      serial_no: "",
      notes: "",
    }];
    dispatch(createInboundShipment({ request_list })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      getAllItems();
      showToast("Success", "success");
      setShowLoader(false);
    });
    setOpenProductDetailsDialog(false);
  };

  const handleProductRemove = () => {
    setInventoryItemDetails((prev) =>
      prev.map((item) =>
        item.id === productDetails.id
          ? { ...item, ...productDetails, quantity: Number(productDetails.quantity) }
          : item
      )
    );
    const request_list = [{
      upc: productDetails?.upc,
      quantity: quantity,
      item_name: productDetails?.item_name,
      status: productDetails?.status ? "GOOD" : "DAMAGED",
      change_type: "MANUAL",
      adjustment_type: "REMOVE",
      serial_no: "",
      notes: "",
    }];
    dispatch(createInboundShipment({ request_list })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      getAllItems();
      showToast("Success", "success");
      setShowLoader(false);
    });
    setOpenProductDetailsDialog(false);
  };

  const isWarehouseStaff = userData?.role === "WAREHOUSE_STAFF";
  const removeDisabled =
    (Number(quantity) > Number(productDetails?.damaged_quantity) && !productDetails?.status) ||
    (Number(quantity) > Number(productDetails?.good_quantity) && productDetails?.status) ||
    Number(quantity) === 0;

  const columns = [
    {
      key: "item_name",
      label: "Item Name",
      width: 380,
      render: (row) => (
        <span
          style={{
            fontSize: "0.875rem",
            minWidth: isMobile ? 0 : 350,
            maxWidth: isMobile ? "100%" : 380,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {row?.item_name}
        </span>
      ),
    },
    { key: "upc", label: "UPC", width: 130 },
    {
      key: "quantity",
      label: "Quantity",
      width: 100,
      align: "center",
      render: (row) => (
        <span
          title={`Good: ${row.good_quantity ?? 0} | Damaged: ${row.damaged_quantity ?? 0}`}
          style={{ cursor: "help" }}
        >
          {row.quantity}
        </span>
      ),
    },
    {
      key: "action",
      label: "Action",
      width: 90,
      align: "center",
      render: (row) => (
        <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Edit Details"
            onClick={() => handleProductDetailsDialogOpen(row)}
          >
            <FilePenLine size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title="View Details"
            onClick={() => handleViewInventory(row)}
          >
            <Eye size={15} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <PageHeader title="Inventory Items" subtitle="Manage your Inventory and Stock" />
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <RefreshButton onRefresh={getAllItems} spinning={showLoader} />
          <SearchBar
            placeholder="Search by name or UPC…"
            searchTerm={searchTerm}
            setSearchTerm={(v) => { setSearchTerm(v); setCurrentPage(0); }}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="default">
                <Filter size={14} /> Sort
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ minWidth: 200 }}>
              <DropdownMenuLabel>Sort by Quantity</DropdownMenuLabel>
            <DropdownMenuItem active={sortDirection === "asc"} onClick={() => { setSortDirection((prev) => prev === "asc" ? null : "asc"); }}>
                <AArrowUp size={14} style={{ marginRight: 8 }} /> Low to High
              </DropdownMenuItem>
            <DropdownMenuItem active={sortDirection === "desc"} onClick={() => { setSortDirection((prev) => prev === "desc" ? null : "desc"); }}>
                <AArrowDown size={14} style={{ marginRight: 8 }} /> High to Low
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "130px" : "160px"}, 1fr))`,
          gap: isMobile ? 10 : 14,
          marginBottom: 20,
        }}
      >
        <div style={statStyle("#00ADB5")}>
          <span style={statLabelStyle}>TOTAL ITEMS</span>
          <span style={statValueStyle}>{inventoryStats.totalItems}</span>
        </div>
        <div style={statStyle("#a78bfa")}>
          <span style={statLabelStyle}>TOTAL QUANTITY</span>
          <span style={statValueStyle}>{inventoryStats.totalQty}</span>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={paginatedRows}
        totalRows={filteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(n) => setCurrentPage(n)}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
        loading={showLoader}
        minWidth={700}
      />

      {/* Product Details Dialog */}
      <Dialog open={openProductDetailsDialog} onOpenChange={handleProductDetailsDialogClose}>
        <DialogContent
          style={{
            maxWidth: isMobile ? "calc(100vw - 16px)" : 440,
            width: isMobile ? "calc(100vw - 16px)" : "90vw",
            maxHeight: "92vh",
            padding: isMobile ? "18px 14px" : "22px 20px",
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ fontSize: isMobile ? "1.05rem" : "1.15rem" }}>Product Details</DialogTitle>
          </DialogHeader>
          <div
            style={{
              backgroundColor: "rgba(255,255,255,0.025)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 10,
              padding: isMobile ? "12px 12px" : "14px 16px",
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? 11 : 13,
            }}
          >
            <div>
              <Label style={{ color: "#FFFFFF99", fontSize: isMobile ? 12 : 13, marginBottom: 5, display: "block", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Name</Label>
              <div
                style={{
                  backgroundColor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 8,
                  padding: "8px 12px",
                  color: "#fff",
                  fontSize: isMobile ? 13 : 13.5,
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                  minHeight: 38,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {productDetails.item_name || "—"}
              </div>
            </div>
            <div>
              <Label style={{ color: "#FFFFFF99", fontSize: isMobile ? 12 : 13, marginBottom: 5, display: "block", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>UPC</Label>
              <div
                style={{
                  backgroundColor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 8,
                  padding: "8px 12px",
                  color: "#fff",
                  fontSize: isMobile ? 13 : 13.5,
                  fontFamily: "monospace",
                  letterSpacing: "0.04em",
                  minHeight: 38,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {productDetails?.upc || "—"}
              </div>
            </div>
            {/* Stock display */}
            <div>
              <Label style={{ color: "#FFFFFF99", fontSize: isMobile ? 12 : 13, marginBottom: 6, display: "block", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Stock</Label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: isMobile ? 6 : 8,
                }}
              >
                <div
                  style={{
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    padding: "7px 4px",
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: isMobile ? "0.8rem" : "0.88rem",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                    flexWrap: "wrap",
                  }}
                >
                  <span>Good:</span>
                  <span>{productDetails.good_quantity ?? 0}</span>
                </div>
                <div
                  style={{
                    backgroundColor: "#FFEBEE",
                    color: "#C62828",
                    padding: "7px 4px",
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: isMobile ? "0.8rem" : "0.88rem",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                    flexWrap: "wrap",
                  }}
                >
                  <span>Damaged:</span>
                  <span>{productDetails.damaged_quantity ?? 0}</span>
                </div>
                <div
                  style={{
                    backgroundColor: "#E3F2FD",
                    color: "#1565C0",
                    padding: "7px 4px",
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: isMobile ? "0.8rem" : "0.88rem",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                    flexWrap: "wrap",
                  }}
                >
                  <span>Total:</span>
                  <span>{productDetails.quantity ?? 0}</span>
                </div>
              </div>
            </div>
            {/* Quantity field with +/- */}
            <div>
              <Label style={{ color: "#FFFFFF99", fontSize: isMobile ? 12 : 13, marginBottom: 6, display: "block", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Quantity</Label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  onClick={handleDecrease}
                  disabled={isWarehouseStaff}
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    cursor: isWarehouseStaff ? "not-allowed" : "pointer",
                    padding: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isWarehouseStaff ? "#555" : "#fff",
                    minWidth: 42,
                    minHeight: 42,
                  }}
                >
                  <MinusCircle size={22} />
                </button>
                <Input
                  type="number"
                  value={quantity}
                  disabled={isWarehouseStaff}
                  onChange={(e) => { const val = e.target.value; if (/^\d*$/.test(val)) setQuantity(val); }}
                  onKeyDown={blockInvalidInt}
                  onPaste={(e) => { if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }}
                  onWheel={(e) => e.target.blur()}
                  style={{ textAlign: "center", flex: 1, height: 42, fontSize: 16, fontWeight: 600, appearance: "textfield", MozAppearance: "textfield" }}
                />
                <button
                  type="button"
                  onClick={handleIncrease}
                  disabled={isWarehouseStaff}
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    cursor: isWarehouseStaff ? "not-allowed" : "pointer",
                    padding: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isWarehouseStaff ? "#555" : "#fff",
                    minWidth: 42,
                    minHeight: 42,
                  }}
                >
                  <PlusCircle size={22} />
                </button>
              </div>
            </div>
            {/* Status switch */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 3 }}>
              <Label style={{ color: "#FFFFFF99", fontSize: isMobile ? 12 : 13, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Status</Label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, color: !productDetails.status ? "#FF5252" : "#777", fontWeight: !productDetails.status ? 700 : 400 }}>Damaged</span>
                <label style={{ position: "relative", display: "inline-block", width: 46, height: 26, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!productDetails.status}
                    onChange={(e) => setProductDetails((prev) => ({ ...prev, status: e.target.checked }))}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: productDetails.status ? "#4CAF50" : "#555",
                      borderRadius: 26,
                      transition: "background-color 0.2s",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 20,
                        width: 20,
                        left: productDetails.status ? 23 : 3,
                        top: 3,
                        backgroundColor: "white",
                        borderRadius: "50%",
                        transition: "left 0.2s",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                      }}
                    />
                  </span>
                </label>
                <span style={{ fontSize: 13, color: productDetails.status ? "#4CAF50" : "#777", fontWeight: productDetails.status ? 700 : 400 }}>Good</span>
              </div>
            </div>
          </div>
          <DialogFooter
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 12,
              marginTop: 14,
              width: "100%",
            }}
          >
            <Button
              style={{
                backgroundColor: removeDisabled ? "rgba(255, 61, 0, 0.08)" : "rgba(255, 61, 0, 0.2)",
                color: removeDisabled ? "rgba(255, 61, 0, 0.45)" : "#FF5252",
                border: removeDisabled ? "1px solid rgba(255, 61, 0, 0.15)" : "1px solid rgba(255, 61, 0, 0.45)",
                flex: 1,
                height: 42,
                borderRadius: 9,
                fontWeight: 700,
                fontSize: 14,
              }}
              onClick={handleProductRemove}
              disabled={removeDisabled}
            >
              Remove
            </Button>
            <Button
              style={{
                backgroundColor: Number(quantity) === 0 ? "rgba(76, 175, 80, 0.08)" : "rgba(76, 175, 80, 0.2)",
                color: Number(quantity) === 0 ? "rgba(76, 175, 80, 0.45)" : "#4CAF50",
                border: Number(quantity) === 0 ? "1px solid rgba(76, 175, 80, 0.15)" : "1px solid rgba(76, 175, 80, 0.45)",
                flex: 1,
                height: 42,
                borderRadius: 9,
                fontWeight: 700,
                fontSize: 14,
              }}
              onClick={handleProductAdd}
              disabled={Number(quantity) === 0}
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Inventory Logs Dialog */}
      <Dialog open={Boolean(viewUpc)} onOpenChange={(open) => { if (!open) { setViewUpc(null); setViewLogs([]); } }}>
        <DialogContent style={{ maxWidth: 1000, width: isMobile ? "calc(100vw - 16px)" : "90vw" }}>
          <DialogHeader>
            <DialogTitle>Product Details</DialogTitle>
            <DialogDescription>Stock log for UPC: {viewUpc}</DialogDescription>
          </DialogHeader>

          {/* Product summary */}
          {(viewProductName || viewProductUpc) && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "140px" : "180px"}, 1fr))`,
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 14px" }}>
                <span style={{ display: "block", color: "#8b9296", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 800 }}>Name</span>
                <span style={{ display: "block", color: "#fff", fontSize: 13, marginTop: 4 }}>{viewProductName}</span>
              </div>
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 14px" }}>
                <span style={{ display: "block", color: "#8b9296", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 800 }}>UPC</span>
                <span style={{ display: "block", color: "#fff", fontSize: 13, marginTop: 4 }}>#{viewProductUpc}</span>
              </div>
            </div>
          )}

          <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", marginBottom: 8 }}>Stock Log</div>
          <DataTable
            columns={viewLogColumns}
            rows={viewLogPaginatedRows}
            totalRows={viewLogs.length}
            currentPage={viewLogPage}
            rowsPerPage={viewLogRpp}
            onPageChange={(n) => setViewLogPage(n)}
            onRowsPerPageChange={(n) => { setViewLogRpp(n); setViewLogPage(0); }}
            loading={viewLoading}
            minWidth={900}
          />
        </DialogContent>
      </Dialog>

      {/* Inventory Image Carousel Dialog */}
      <Dialog open={viewImagesOpen} onOpenChange={() => setViewImagesOpen(false)}>
        <DialogContent style={{ maxWidth: isMobile ? "95vw" : 520 }}>
          <DialogHeader>
            <DialogTitle>Images</DialogTitle>
          </DialogHeader>
          {viewImages.length > 0 ? (
            <>
              <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: isMobile ? 180 : 300, padding: "0 44px" }}>
                <button
                  onClick={() => setViewImageIdx((prev) => (prev > 0 ? prev - 1 : viewImages.length - 1))}
                  disabled={viewImages.length <= 1}
                  style={{ position: "absolute", left: 0, background: "none", border: "none", cursor: viewImages.length <= 1 ? "default" : "pointer", padding: 4, color: "white", opacity: viewImages.length <= 1 ? 0.3 : 1, display: "flex", alignItems: "center" }}
                >
                  <ChevronLeft size={28} />
                </button>
                <img
                  src={`data:${viewImages[viewImageIdx].content_type};base64,${viewImages[viewImageIdx].document_bytes}`}
                  alt={`Item image ${viewImageIdx + 1}`}
                  style={{ maxWidth: "100%", maxHeight: isMobile ? 180 : 350, borderRadius: 8, objectFit: "contain", display: "block" }}
                />
                <button
                  onClick={() => setViewImageIdx((prev) => (prev < viewImages.length - 1 ? prev + 1 : 0))}
                  disabled={viewImages.length <= 1}
                  style={{ position: "absolute", right: 0, background: "none", border: "none", cursor: viewImages.length <= 1 ? "default" : "pointer", padding: 4, color: "white", opacity: viewImages.length <= 1 ? 0.3 : 1, display: "flex", alignItems: "center" }}
                >
                  <ChevronRight size={28} />
                </button>
              </div>
              <div style={{ textAlign: "center", marginTop: 8, fontSize: 13, color: "#888" }}>
                {viewImageIdx + 1} / {viewImages.length}
              </div>
            </>
          ) : (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: isMobile ? 180 : 300, color: "#888" }}>
              No image available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseInventory;
